import React, { CSSProperties, ReactNode, useEffect, useRef } from 'react';

interface CardTableProps {
    children: ReactNode;
    style?: CSSProperties;
    /** Extra classes. `rw-card-table` is always applied. */
    className?: string;
    /**
     * 0-based column indices whose cells hold controls rather than data. They move to the foot of
     * the card and lay out as a row.
     */
    actionColumns?: number[];
    /** As above, but the cell holds only icon buttons, which keep their natural size. */
    iconActionColumns?: number[];
    /** Columns holding free text: they take the full card width and read after the short fields. */
    wideColumns?: number[];
}

/**
 * A table that reads as a table on a desktop and as a list of cards below 1024px.
 *
 * The card layout hides the header row, so every cell has to name its own field. Rather than hand
 * writing a `data-label` onto every `<td>` -- which is how DownloadsPage and UsersPage do it, and
 * which is fine for one or two tables -- this copies the caption off the matching `<th>` at
 * runtime. Across the fifteen-odd admin tables that is the difference between one changed line per
 * table and a hundred, and a column renamed in the header can no longer disagree with the caption
 * shown on a phone.
 *
 * A cell that already carries `data-label` is left alone, so a caption can still be overridden.
 *
 * The observer is needed because the body re-renders on every sort, filter and page change; a
 * one-shot effect would label the first page and leave every later one blank.
 */
export const CardTable: React.FC<CardTableProps> = ({
    children,
    style,
    className,
    actionColumns,
    iconActionColumns,
    wideColumns,
}) => {
    const tableRef = useRef<HTMLTableElement>(null);
    // Read through refs so the observer callback never goes stale and the effect need not re-run
    // when a caller passes fresh array literals on every render.
    const columnRoles = useRef({ actionColumns, iconActionColumns, wideColumns });
    columnRoles.current = { actionColumns, iconActionColumns, wideColumns };

    useEffect(() => {
        const table = tableRef.current;
        if (!table) return;

        const apply = () => {
            const headers = Array.from(table.querySelectorAll<HTMLTableCellElement>('thead th'))
                .map((th) => {
                    // Headers break their labels with <br> to keep narrow columns narrow, and
                    // textContent would run the halves together -- "Admin<br/>Contacts" read as
                    // "AdminContacts" and then broke mid-word in the caption column.
                    const clone = th.cloneNode(true) as HTMLElement;
                    for (const br of Array.from(clone.querySelectorAll('br'))) br.replaceWith(' ');
                    return (clone.textContent || '').replace(/\s+/g, ' ').trim();
                });
            const roles = columnRoles.current;

            // A column is treated as the card's footer when its header says so, or says nothing --
            // the near-universal shape for a trailing (or leading) column of row controls. Icon
            // mode when none of its buttons carry a label, so a 30px eye icon is not stretched to
            // fill half the card. Explicit props still win.
            const inferred = headers.map((caption, index) => {
                if (roles.actionColumns?.includes(index) || roles.iconActionColumns?.includes(index)) {
                    return null;
                }
                if (caption !== '' && !/^actions?$/i.test(caption)) return null;
                const cells = table.querySelectorAll<HTMLTableCellElement>(
                    `tbody tr > *:nth-child(${index + 1})`
                );
                let buttons = 0;
                let labelled = 0;
                for (const cell of Array.from(cells)) {
                    for (const control of Array.from(cell.querySelectorAll('button, a'))) {
                        buttons++;
                        if ((control.textContent || '').trim()) labelled++;
                    }
                }
                if (buttons === 0) return null;
                return labelled === 0 ? 'icons' : 'labelled';
            });

            for (const row of Array.from(table.querySelectorAll<HTMLTableRowElement>('tbody tr, tfoot tr'))) {
                Array.from(row.cells).forEach((cell, index) => {
                    const caption = headers[index];
                    if (caption && !cell.hasAttribute('data-label')) {
                        cell.setAttribute('data-label', caption);
                    }
                    if (roles.actionColumns?.includes(index)) {
                        cell.classList.add('rw-card-actions');
                    }
                    if (roles.iconActionColumns?.includes(index)) {
                        cell.classList.add('rw-card-actions', 'rw-card-actions-icons');
                    }
                    if (roles.wideColumns?.includes(index)) {
                        cell.classList.add('rw-card-wide', 'rw-card-last');
                    }
                    if (inferred[index] === 'labelled') {
                        cell.classList.add('rw-card-actions');
                    } else if (inferred[index] === 'icons') {
                        cell.classList.add('rw-card-actions', 'rw-card-actions-icons');
                    }
                });
            }
        };

        apply();
        const observer = new MutationObserver(apply);
        observer.observe(table, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);

    return (
        <table ref={tableRef} className={`rw-card-table${className ? ` ${className}` : ''}`} style={style}>
            {children}
        </table>
    );
};
